import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Plus,
  Edit,
  Trash2,
  Loader2,
  FileText,
  Hash,
  Calendar,
  CheckSquare,
  List,
  Upload,
  Type,
  AlignLeft,
  X
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const FIELD_TYPES = [
  { value: 'text', label: 'Text', icon: Type },
  { value: 'long_text', label: 'Long Text', icon: AlignLeft },
  { value: 'number', label: 'Number', icon: Hash },
  { value: 'date', label: 'Date', icon: Calendar },
  { value: 'date_picker', label: 'Date Picker', icon: Calendar },
  { value: 'dropdown', label: 'Dropdown', icon: List },
  { value: 'checkbox', label: 'Checkbox', icon: CheckSquare },
  { value: 'file_upload', label: 'File Upload', icon: Upload },
];

const getFieldIcon = (type) => {
  const field = FIELD_TYPES.find(f => f.value === type);
  return field?.icon || FileText;
};

export const PatientCustomFieldsTab = ({ t }) => {
  const [fields, setFields] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedField, setSelectedField] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    field_type: 'text',
    group_id: '',
    description: '',
    placeholder: '',
    required: false,
    display_order: 0,
    options: [],
    allowed_file_types: [],
    max_file_size_mb: 5,
    status: 'active'
  });
  const [saving, setSaving] = useState(false);
  const [newOption, setNewOption] = useState({ value: '', label: '' });
  const [groupFilter, setGroupFilter] = useState('all');

  useEffect(() => {
    fetchGroups();
    fetchFields();
  }, []);

  const fetchGroups = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/patient-custom-fields/groups`);
      setGroups(response.data);
    } catch (error) {
      console.error('Error fetching groups:', error);
    }
  };

  const fetchFields = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/patient-custom-fields/fields`);
      setFields(response.data);
    } catch (error) {
      console.error('Error fetching fields:', error);
      toast.error(t?.('customFields.errorLoading') || 'Error loading fields');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenForm = (field = null) => {
    if (field) {
      setSelectedField(field);
      setFormData({
        name: field.name || '',
        field_type: field.field_type || 'text',
        group_id: field.group_id || '',
        description: field.description || '',
        placeholder: field.placeholder || '',
        required: field.required || false,
        display_order: field.display_order || 0,
        options: field.options || [],
        allowed_file_types: field.allowed_file_types || [],
        max_file_size_mb: field.max_file_size_mb || 5,
        status: field.status || 'active'
      });
    } else {
      setSelectedField(null);
      setFormData({
        name: '',
        field_type: 'text',
        group_id: groups[0]?.id || '',
        description: '',
        placeholder: '',
        required: false,
        display_order: fields.length,
        options: [],
        allowed_file_types: ['.pdf', '.jpg', '.png', '.doc', '.docx'],
        max_file_size_mb: 5,
        status: 'active'
      });
    }
    setNewOption({ value: '', label: '' });
    setIsFormOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.group_id) {
      toast.error(t?.('customFields.selectGroup') || 'Please select a group');
      return;
    }
    setSaving(true);
    try {
      if (selectedField) {
        await axios.put(`${API_URL}/api/patient-custom-fields/fields/${selectedField.id}`, formData);
        toast.success(t?.('customFields.fieldUpdated') || 'Field updated successfully');
      } else {
        await axios.post(`${API_URL}/api/patient-custom-fields/fields`, formData);
        toast.success(t?.('customFields.fieldCreated') || 'Field created successfully');
      }
      fetchFields();
      setIsFormOpen(false);
    } catch (error) {
      const message = error.response?.data?.detail || 'Error saving field';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (field) => {
    if (!window.confirm(t?.('customFields.confirmDeleteField') || `Are you sure you want to delete "${field.name}"?`)) {
      return;
    }
    try {
      await axios.delete(`${API_URL}/api/patient-custom-fields/fields/${field.id}`);
      toast.success(t?.('customFields.fieldDeleted') || 'Field deleted successfully');
      fetchFields();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error deleting field');
    }
  };

  const addOption = () => {
    if (!newOption.value || !newOption.label) return;
    setFormData({
      ...formData,
      options: [...formData.options, { ...newOption }]
    });
    setNewOption({ value: '', label: '' });
  };

  const removeOption = (index) => {
    setFormData({
      ...formData,
      options: formData.options.filter((_, i) => i !== index)
    });
  };

  const filteredFields = groupFilter === 'all' 
    ? fields 
    : fields.filter(f => f.group_id === groupFilter);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center gap-4">
        <div className="flex items-center gap-4">
          <Select value={groupFilter} onValueChange={setGroupFilter}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder={t?.('customFields.filterByGroup') || 'Filter by group'} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t?.('common:all') || 'All Groups'}</SelectItem>
              {groups.map((g) => (
                <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-sm text-muted-foreground">
            {filteredFields.length} {t?.('customFields.fields') || 'fields'}
          </span>
        </div>
        <Button 
          onClick={() => handleOpenForm()} 
          className="bg-primary hover:bg-primary/90"
          disabled={groups.length === 0}
        >
          <Plus className="h-4 w-4 mr-2" />
          {t?.('customFields.newField') || 'New Field'}
        </Button>
      </div>

      {groups.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FileText className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground text-center">
              {t?.('customFields.createGroupFirst') || 'Please create a group first before adding custom fields.'}
            </p>
          </CardContent>
        </Card>
      ) : filteredFields.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FileText className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">
              {t?.('customFields.noFields') || 'No custom fields created yet.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {filteredFields.map((field) => {
            const Icon = getFieldIcon(field.field_type);
            return (
              <Card key={field.id} className="hover:shadow-md transition-shadow">
                <CardContent className="flex items-center justify-between p-4">
                  <div className="flex items-center gap-4">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Icon className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-foreground">{field.name}</h3>
                        {field.required && (
                          <Badge variant="destructive" className="text-xs">Required</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <span>{FIELD_TYPES.find(t => t.value === field.field_type)?.label || field.field_type}</span>
                        <span>•</span>
                        <span>{field.group_name}</span>
                      </div>
                      {field.description && (
                        <p className="text-xs text-muted-foreground mt-1">{field.description}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={field.status === 'active' ? 'default' : 'secondary'}>
                      {field.status === 'active' ? 'Active' : 'Inactive'}
                    </Badge>
                    <Button variant="ghost" size="icon" onClick={() => handleOpenForm(field)}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(field)} className="text-destructive hover:text-destructive">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Form Dialog */}
      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedField ? (t?.('customFields.editField') || 'Edit Field') : (t?.('customFields.newField') || 'New Field')}
            </DialogTitle>
            <DialogDescription>
              {t?.('customFields.fieldFormDescription') || 'Configure the custom field properties.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t?.('customFields.fieldName') || 'Field Name'} *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={t?.('customFields.fieldNamePlaceholder') || 'e.g., Blood Type'}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>{t?.('customFields.fieldType') || 'Field Type'} *</Label>
                <Select value={formData.field_type} onValueChange={(v) => setFormData({ ...formData, field_type: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FIELD_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        <div className="flex items-center gap-2">
                          <type.icon className="h-4 w-4" />
                          {type.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t?.('customFields.group') || 'Group'} *</Label>
                <Select value={formData.group_id} onValueChange={(v) => setFormData({ ...formData, group_id: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder={t?.('customFields.selectGroup') || 'Select group'} />
                  </SelectTrigger>
                  <SelectContent>
                    {groups.filter(g => g.status === 'active').map((g) => (
                      <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t?.('customFields.displayOrder') || 'Display Order'}</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.display_order}
                  onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t?.('customFields.description') || 'Description'}</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder={t?.('customFields.descriptionPlaceholder') || 'Help text for this field'}
              />
            </div>

            <div className="space-y-2">
              <Label>{t?.('customFields.placeholder') || 'Placeholder'}</Label>
              <Input
                value={formData.placeholder}
                onChange={(e) => setFormData({ ...formData, placeholder: e.target.value })}
                placeholder={t?.('customFields.placeholderPlaceholder') || 'Placeholder text shown in the input'}
              />
            </div>

            {/* Dropdown Options */}
            {formData.field_type === 'dropdown' && (
              <div className="space-y-2">
                <Label>{t?.('customFields.dropdownOptions') || 'Dropdown Options'}</Label>
                <div className="flex gap-2">
                  <Input
                    value={newOption.value}
                    onChange={(e) => setNewOption({ ...newOption, value: e.target.value })}
                    placeholder="Value"
                    className="flex-1"
                  />
                  <Input
                    value={newOption.label}
                    onChange={(e) => setNewOption({ ...newOption, label: e.target.value })}
                    placeholder="Label"
                    className="flex-1"
                  />
                  <Button type="button" variant="outline" onClick={addOption}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                {formData.options.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.options.map((opt, idx) => (
                      <Badge key={idx} variant="secondary" className="cursor-pointer" onClick={() => removeOption(idx)}>
                        {opt.label} ({opt.value}) <X className="h-3 w-3 ml-1" />
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* File Upload Settings */}
            {formData.field_type === 'file_upload' && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t?.('customFields.allowedFileTypes') || 'Allowed File Types'}</Label>
                  <Input
                    value={formData.allowed_file_types.join(', ')}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      allowed_file_types: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                    })}
                    placeholder=".pdf, .jpg, .png"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t?.('customFields.maxFileSize') || 'Max File Size (MB)'}</Label>
                  <Input
                    type="number"
                    min="1"
                    max="50"
                    value={formData.max_file_size_mb}
                    onChange={(e) => setFormData({ ...formData, max_file_size_mb: parseInt(e.target.value) || 5 })}
                  />
                </div>
              </div>
            )}

            <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
              <div>
                <Label>{t?.('customFields.required') || 'Required Field'}</Label>
                <p className="text-xs text-muted-foreground">
                  {t?.('customFields.requiredDescription') || 'Make this field mandatory when creating/editing patients'}
                </p>
              </div>
              <Switch
                checked={formData.required}
                onCheckedChange={(v) => setFormData({ ...formData, required: v })}
              />
            </div>

            <div className="space-y-2">
              <Label>{t?.('common:status') || 'Status'}</Label>
              <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">{t?.('common:active') || 'Active'}</SelectItem>
                  <SelectItem value="inactive">{t?.('common:inactive') || 'Inactive'}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsFormOpen(false)}>
                {t?.('common:cancel') || 'Cancel'}
              </Button>
              <Button type="submit" className="bg-primary hover:bg-primary/90" disabled={saving || !formData.name || !formData.group_id}>
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {selectedField ? (t?.('common:update') || 'Update') : (t?.('common:create') || 'Create')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PatientCustomFieldsTab;
